<template>
    <ModalContent stitch>
        <template slot="title">{{ title }}</template>
        <template slot="content">
            <h6 class="stitch-modal__title">Information</h6>
            <v-row>
                <v-col cols="12" sm="6">
                    <Field
                        v-model="establishment.name"
                        :error-messages="establishment_saving_errors?.name"
                        :label="$translate('labels.name')"
                        name="name"
                        autofocus
                        required
                        @keydown="handleKeyPress"
                    />
                </v-col>

                <v-col cols="12" sm="6">
                    <Field
                        v-model="establishment.code"
                        :error-messages="establishment_saving_errors?.code"
                        :label="$translate('labels.code_automatically_generated')"
                        name="code"
                        required
                        @keydown="handleKeyPress"
                    />
                </v-col>
            </v-row>

            <v-row class="mt-2">
                <v-col cols="12" sm="6">
                    <v-select
                        v-model="establishment.billingaccount"
                        :items="billing_accounts"
                        :label="$translate('labels.billingAccount')"
                        :error-messages="establishment_saving_errors?.billingaccount"
                        item-text="name"
                        item-value="id"
                        :clearable="true"
                        @keydown="handleKeyPress"
                     outlined  dense />
                </v-col>
                <v-col cols="12" sm="6">
                    <v-checkbox
                        v-model="establishment.main"
                        :label="$translate('labels.mainEstablishment')"
                        hide-details
                        class="mt-0"
                    />
                    <p class="text-caption text--secondary mb-0 mt-2">
                        {{ $translate("mainEstablishmentExplanation") }}
                    </p>
                </v-col>
            </v-row>

            <AddressForm
                class="mt-0"
                :title="$translate('labels.address')"
                v-model="establishment.address"
                :errors="establishment_saving_errors?.address"
                @keydown="handleKeyPress"
            />
        </template>
        <template slot="bottom">
            <Button
                :text="$translate('buttons.cancel')"
                class="my-2 my-sm-4 w-100 w-sm-auto"
                @click="cancelEditClicked"
                ghost
            />

            <Button
                class="my-2 my-sm-4 w-100 w-sm-auto ml-sm-3"
                :text="save_text"
                @click="saveInformationClicked"
            />
        </template>
    </ModalContent>
</template>

<script>
import { mapState, mapActions } from "vuex";
import GlobalMixins from "@/mixins/global";
import axios from "@/plugins/axios";

export default {
    mixins: [GlobalMixins],
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapState("establishment", [
            "establishment_saving",
            "establishment_saving_errors",
        ]),
        isNew() {
            return !this.establishment.id;
        },
        save_text() {
            if (this.isNew) {
                return this.$translate("buttons.createEstablishment");
            }
            return this.$translate("buttons.save");
        },
        title() {
            return this.params.establishment.id
                ? this.$translate("messages.editEstablishment")
                : this.$translate("messages.newEstablishment");
        },
    },
    data() {
        let establishment = JSON.parse(JSON.stringify(this.params.establishment || {}));
        const isNew = !establishment.id;

        if (isNew) {
            if (!establishment.name) {
                const existingCount =
                    this.$store.getters["session/stats"]?.establishments || 0;
                establishment.name = this.$translate(
                    "messages.defaultEstablishmentName",
                    { number: existingCount + 1 },
                );
            }
            if (
                !establishment.address ||
                !Object.keys(establishment.address).length
            ) {
                const entityAddress =
                    this.$store.getters["session/current_entity"]
                        ?.billing_address;
                if (entityAddress) {
                    const address = JSON.parse(JSON.stringify(entityAddress));
                    // Drop the entity address PK so saving creates a new address
                    // instead of mutating the entity's billing address.
                    delete address.id;
                    establishment.address = address;
                }
            }
        }

        if (!establishment.address) {
            establishment.address = {};
        }
        return {
            establishment: establishment,
            billing_accounts: [],
        };
    },
    async mounted() {
        // Fetch billing accounts
        try {
            const response = await axios.get('/api/billingaccounts/', {
                params: { archived: false },
            });
            this.billing_accounts = response.data.results || [];
        } catch (error) {
            console.error('Failed to fetch billing accounts', error);
        }
    },
    methods: Object.assign(
        mapActions("mcl", ["hideModal"]),
        mapActions("editestablishment", ["saveEstablishment"]),
        {
            handleKeyPress(event) {
                if (event.key === "Enter") {
                    event.preventDefault();
                    if (!this.establishment_saving) {
                        this.saveInformationClicked();
                    }
                }
            },

            saveInformationClicked() {
                window.setTimeout(async () => {
                    try {
                        const response = await this.saveEstablishment({
                            instance: this.establishment,
                        });
                        if (response.status == 201) {
                            this.addAlert(this.$translate("messages.establishmentCreated"));
                        } else {
                            this.addAlert(this.$translate("messages.establishmentUpdated"));
                        }
                        this.hideModal({ result: response.data });
                    } catch (error) {
                        this.$swal({
                            title: this.$translate("messages.failedSavingEstablishment"),
                            html: this.$utils.resultToSwalContent(this, error),
                            type: "error",
                        });
                    }
                }, 0);
            },
            cancelEditClicked() {
                this.hideModal();
            },
        }
    ),
    i18n: {
        messages: {
            en: {
                labels: {
                    name: "Name",
                    code_automatically_generated: "Code (automatically generated if empty)",
                    address: "Address",
                    billingAccount: "Billing account",
                    mainEstablishment: "Main establishment",
                },
                buttons: {
                    cancel: "Cancel",
                    createEstablishment: "Create establishment",
                    save: "Save",
                },
                messages: {
                    editEstablishment: "Edit establishment",
                    newEstablishment: "New establishment",
                    establishmentCreated: "Establishment created",
                    establishmentUpdated: "Establishment updated",
                    failedSavingEstablishment: "Failed saving establishment",
                    defaultEstablishmentName: "Establishment {number}",
                },
                mainEstablishmentExplanation:
                    "The main establishment is used as default for business operations.",
            },
            fr: {
                labels: {
                    name: "Nom",
                    code_automatically_generated: "Code (généré automatiquement si vide)",
                    address: "Adresse",
                    billingAccount: "Compte de facturation",
                    mainEstablishment: "Établissement principal",
                },
                buttons: {
                    cancel: "Annuler",
                    createEstablishment: "Créer l'établissement",
                    save: "Enregistrer",
                },
                messages: {
                    editEstablishment: "Modifier l'établissement",
                    newEstablishment: "Nouvel établissement",
                    establishmentCreated: "Établissement créé",
                    establishmentUpdated: "Établissement mis à jour",
                    failedSavingEstablishment: "Erreur lors de la sauvegarde de l'établissement",
                    defaultEstablishmentName: "Établissement {number}",
                },
                mainEstablishmentExplanation:
                    "L'établissement principal est utilisé par défaut pour les opérations commerciales.",
            },
        },
    },
};
</script>

